/**
 * Municipality roles service – CRUD for municipality_roles collection.
 *
 * Phase 2.8 – USER_ROLES.md
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import type { Timestamp, UpdateData } from 'firebase/firestore';
import { db } from '@municipality/firebase';
import type {
  MunicipalityRole,
  MunicipalityRoleCreateInput,
  MunicipalityRoleUpdateInput,
  RolePermissions,
} from '@municipality/types';
import { assertValidRoleId } from '@municipality/types';

const COLLECTION = 'municipality_roles';

function toDate(value: Timestamp | Date | { seconds: number; nanoseconds: number } | undefined): Date | undefined {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    return (value as Timestamp).toDate();
  }
  if (typeof value === 'object' && 'seconds' in value) {
    return new Date((value as { seconds: number }).seconds * 1000);
  }
  return undefined;
}

function mapRoleDoc(data: Record<string, unknown>, id: string): MunicipalityRole {
  const createdAt = toDate(data.createdAt as Timestamp | undefined) ?? new Date();
  const updatedAt = toDate(data.updatedAt as Timestamp | undefined) ?? new Date();
  return {
    id,
    name: (data.name as string) ?? '',
    description: data.description as string | undefined,
    isSystem: data.isSystem === true,
    isArchived: data.isArchived === true,
    permissions: (data.permissions as RolePermissions) ?? {},
    createdAt,
    updatedAt,
    createdBy: data.createdBy as string | undefined,
  };
}

/** Fetch all roles, optionally filtering archived. Uses client-side filter for reliability. */
export async function fetchMunicipalityRoles(options?: {
  includeArchived?: boolean;
}): Promise<MunicipalityRole[]> {
  const rolesRef = collection(db, COLLECTION);
  const q = query(rolesRef, orderBy('updatedAt', 'desc'));
  const snapshot = await getDocs(q);
  const roles = snapshot.docs.map((d) => mapRoleDoc(d.data() as Record<string, unknown>, d.id));
  if (options?.includeArchived === false) {
    return roles.filter((r) => !r.isArchived);
  }
  return roles;
}

/** Fetch a single role by ID. */
export async function fetchMunicipalityRole(roleId: string): Promise<MunicipalityRole | null> {
  assertValidRoleId(roleId);
  const ref = doc(db, COLLECTION, roleId);
  const snapshot = await getDoc(ref);
  if (!snapshot.exists()) return null;
  return mapRoleDoc(snapshot.data() as Record<string, unknown>, snapshot.id);
}

/** Create a new municipality role. */
export async function createMunicipalityRole(
  input: MunicipalityRoleCreateInput,
  createdBy?: string
): Promise<MunicipalityRole> {
  assertValidRoleId(input.id);
  const ref = doc(db, COLLECTION, input.id);
  const now = serverTimestamp();
  const data = {
    id: input.id,
    name: input.name,
    description: input.description ?? null,
    isSystem: input.isSystem,
    isArchived: input.isArchived ?? false,
    permissions: input.permissions,
    createdAt: now,
    updatedAt: now,
    createdBy: createdBy ?? null,
  };
  await setDoc(ref, data);
  const created = await getDoc(ref);
  return mapRoleDoc(created.data() as Record<string, unknown>, created.id);
}

/** Update an existing municipality role. */
export async function updateMunicipalityRole(
  roleId: string,
  input: MunicipalityRoleUpdateInput
): Promise<void> {
  assertValidRoleId(roleId);
  const ref = doc(db, COLLECTION, roleId);
  const updates: UpdateData<Record<string, unknown>> = {
    updatedAt: serverTimestamp(),
  };
  if (input.name !== undefined) updates.name = input.name;
  if (input.description !== undefined) updates.description = input.description;
  if (input.isArchived !== undefined) updates.isArchived = input.isArchived;
  if (input.permissions !== undefined) updates.permissions = input.permissions;
  await updateDoc(ref, updates);
}
